// Curated, user-facing release notes for the Replay QA app.
//
// This is a hand-curated, customer-safe view of the most recent product changes, written from the
// deploy stream and the PRs behind it. Nothing internal belongs here verbatim — DB tables, function
// names, k8s, tunnels — so each note is written in plain language and stripped of implementation
// detail.
//
// Audience: all signed-in users. The /release-notes page and its user-menu entry are visible to
// every authenticated user (not admin-gated), because the content here is public-facing product
// news with no internal-only information. If a future note describes internal-only work, leave it
// out of this list rather than gating the page.
//
// ─────────────────────────────────────────────────────────────────────────────────────────────
// MILESTONES vs MINOR UPDATES
// ─────────────────────────────────────────────────────────────────────────────────────────────
// We ship to production continuously — there are many more deploys than there are entries here.
// Listing every deploy would be noise, so each release is classified with a `kind`:
//
//   • 'milestone' — a whole new capability or a change worth announcing on its own. Rendered as a
//     full card. These are the headline features (the original release-notes entries).
//
//   • 'minor' — a smaller, self-contained update that shipped between milestones and is still
//     worth surfacing. Rendered in a lighter, compact timeline style so it reads as secondary.
//
// How to decide whether a deploy is "meaningful" enough to list at all (the rule we apply when
// scanning the deploy stream for candidate minor updates):
//
//   INCLUDE a deploy as a 'minor' release when it is BOTH customer-visible AND self-contained,
//   i.e. it does at least one of:
//     - fixes a bug a real user could have hit,
//     - makes a small but noticeable UX / quality / performance improvement,
//     - changes a price, limit, label, or copy that users actually see.
//
//   OMIT the deploy entirely (do not list it) when it is none of the above — e.g. internal
//   refactors, infrastructure / CI / deploy plumbing, admin-only tooling, docs, test-only
//   changes, dependency bumps with no user-facing effect, and marker / merge / no-op deploys.
//
//   PROMOTE a 'minor' to a 'milestone' when it grows into a whole new capability rather than an
//   improvement to an existing one.
//
// When a deploy lands something notable and user-facing, add a corresponding note to this list
// (the array is sorted for display at the bottom of this file, so insertion order does not
// matter). `date` is the day the change landed; `title` is a short headline; `summary` is a 1-2
// sentence plain-language description; `kind` classifies it per the rules above.
//
// ─────────────────────────────────────────────────────────────────────────────────────────────
// ADDING A NOTE IS A SINGLE, SELF-CONTAINED EDIT (see bug #1118)
// ─────────────────────────────────────────────────────────────────────────────────────────────
// Each note owns its own `date` — there is no second data structure to keep in sync. Previously the
// displayed date was sourced from a separate hand-maintained `loopBuilderDeployDates` map keyed by
// build id; a note whose build was missing from that map resolved to an empty date and silently
// sorted to the BOTTOM of the newest-first list, so new entries never showed up at the top. That
// map stopped being maintained after the late-June builds, which is exactly why new major/minor
// notes stopped appearing (bug #1118). The map is gone: set `date` directly on the entry to the
// build's ACTUAL deploy date (NOT the day you happen to be editing this file — that is the drift
// that caused bug #1036).
// `date` is required, so a note can never be added in a state where it fails to appear.

export type ReleaseKind = 'milestone' | 'minor'

export interface ReleaseNote {
  /**
   * ISO date (YYYY-MM-DD) the change actually landed in production — the build's real Loop Builder
   * deploy date. Required and the single source of truth for both the displayed label and the
   * newest-first ordering. Set it to the day the change shipped, NOT the day you edit this file
   * (that reused-curation-day mistake is bug #1036). Resolve a note's date with `releaseDate()`.
   */
  date: string
  /** Short, plain-language headline. */
  title: string
  /** 1-2 sentence customer-facing summary. */
  summary: string
  /**
   * Whether this is a headline 'milestone' (card) or a smaller 'minor' update (compact timeline
   * row). See the "MILESTONES vs MINOR UPDATES" note above for how to classify a deploy.
   */
  kind: ReleaseKind
}

/**
 * The effective date to display and sort a release by — the entry's own `date`, which is the single
 * source of truth (see the "ADDING A NOTE" note above and bug #1118). Kept as a small helper so the
 * page has one place to call and the sort/display can never diverge on how a note's date is derived.
 */
export function releaseDate(note: Pick<ReleaseNote, 'date'>): string {
  return note.date
}

// Unsorted source list — order here does not matter; `releaseNotes` (below) sorts for display.
const entries: ReleaseNote[] = [
  // ── Milestones ──────────────────────────────────────────────────────────────────────────────
  // `date` is the day each change actually shipped — set it to the build's real Loop Builder deploy
  // date, NOT the day you edit this file (that reuse-the-curation-day mistake is bug #1036). The
  // late-June GitHub/PR/budget/globals batch below rolled out across 06-20…06-30, on its own days.
  //
  // The July/August entries below shipped via the regular dev/deploy pipeline and carry their own
  // `date`.
  {
    date: '2026-09-18',
    title: 'File bugs into GitLab Issues',
    kind: 'minor',
    summary:
      'GitLab joins GitHub, Linear, and Jira as an issue tracker you can connect from the project chat. Point Replay at a GitLab project with an access token and every bug it finds is filed there as an issue — complete with steps, screenshots, root-cause analysis, and recording links.',
  },
  {
    date: '2026-08-30',
    title: 'Give a project multiple environments',
    kind: 'milestone',
    summary:
      'A project can now have more than one environment. Set them up straight from the project chat or during onboarding, run QA against each, and schedule recurring runs in your own timezone — every test run, journey, and bug shows which environment it came from.',
  },
  {
    date: '2026-08-25',
    title: 'Refreshed plans, now with Enterprise',
    kind: 'milestone',
    summary:
      'The plan picker has updated Starter and Pro plans plus new Enterprise, Private Cloud, and On-Prem tiers. Team collaboration — inviting teammates into a shared project that runs on one plan’s credits — is included on Pro and above.',
  },
  {
    date: '2026-08-20',
    title: 'Edit project settings right in the app',
    kind: 'milestone',
    summary:
      'Project Settings is now fully editable from the UI. Update a project’s configuration directly on the page instead of only through chat.',
  },
  {
    date: '2026-08-19',
    title: 'QA apps that live behind a login or run locally',
    kind: 'milestone',
    summary:
      'Onboarding can now point Replay at an app that requires authentication or runs on your own machine, connecting through a secure tunnel so QA can reach it.',
  },
  {
    date: '2026-08-05',
    title: 'Invite your team to a project',
    kind: 'milestone',
    summary:
      'Projects are now something you share with your team. Invite teammates from a project and they get a branded invite email, sign in through a tailored landing page, and chat with QA as themselves — and everyone on the project, not just its owner, gets the QA-completion emails.',
  },
  {
    date: '2026-08-04',
    title: 'Every QA round, organized into Test Runs',
    kind: 'milestone',
    summary:
      'A new Test Runs view groups each round of QA — whether you start it yourself or it fires from a pull request — into a single run with its own detail page. Open a run to see every journey, bug, and result that was tested together. Now on by default for every project.',
  },
  {
    date: '2026-07-13',
    title: 'A chat-first way to run QA',
    kind: 'milestone',
    summary:
      'Starting and following a project is now a guided conversation: describe your app in chat to kick off QA, and each project opens in a chat-based workspace where Replay keeps you posted and answers questions while testing runs. Previously an opt-in preview, this is now the default for everyone.',
  },
  {
    date: '2026-07-13',
    title: 'A redesigned project overview',
    kind: 'milestone',
    summary:
      'Every project now opens on a single, streamlined overview page that brings its journeys, bugs, and latest QA results together in one place. Once an opt-in preview, it is now the default view.',
  },
  {
    date: '2026-07-13',
    title: 'Send bugs straight to your issue tracker',
    kind: 'milestone',
    summary:
      'Connect a project to GitHub, Linear, or Jira from the project chat and Replay files every bug it finds as an issue there — complete with steps, screenshots, root-cause analysis, and recording links — so fixes start in the workflow you already use.',
  },
  {
    date: '2026-07-08',
    title: 'QA works past login screens with you',
    kind: 'milestone',
    summary:
      'When QA hits a login screen it can’t get past on its own, testing now pauses and asks you in the project’s QA chat how to proceed — hand it credentials, let it create a test account, or skip the signed-in areas — and it resumes automatically once you answer.',
  },
  {
    date: '2026-06-30',
    title: 'Test apps hosted on GitHub',
    kind: 'milestone',
    summary:
      'You can now point a project at a GitHub repository instead of a public URL. Replay clones the repo, runs it locally — with optional mocked backend services — and runs QA against it.',
  },
  {
    date: '2026-06-29',
    title: 'QA on every pull request',
    kind: 'milestone',
    summary:
      'Connect a GitHub-backed project and Replay automatically runs QA on each pushed revision of a pull request, posting the results as a PR comment and check with a live approved / blocked status.',
  },
  {
    date: '2026-06-28',
    title: 'Bug reports inline in PR comments',
    kind: 'milestone',
    summary:
      'When QA finds issues in a pull request, the most severe bugs are now summarized directly in the PR comment, each linking to the full report and scoped to that PR’s version.',
  },
  {
    date: '2026-06-26',
    title: 'Automatic QA for new deploys',
    kind: 'milestone',
    summary:
      'Replay now watches for newly deployed versions, discovers what changed, updates the journeys that cover it, and files any bugs against that version. Every task, journey, and exploration shows a Target URL badge for the exact build it was tested against.',
  },
  {
    date: '2026-06-25',
    title: 'Per-project credit budgets',
    kind: 'milestone',
    summary:
      'Each project now has its own credit budget — a spend cap layered on top of your account balance. Set it when creating a project, and resume a paused project inline by raising its budget.',
  },
  {
    date: '2026-06-23',
    title: 'Global Variables in Project Settings',
    kind: 'milestone',
    summary:
      'Project Settings has a new Global Variables section for editing the values shared across a project’s journeys (referenced as ${name}) — handy for shared fixtures such as a test account’s credentials.',
  },
  {
    date: '2026-06-22',
    title: 'A warmer welcome for shared links',
    kind: 'milestone',
    summary:
      'Visitors who open a shared project link while signed out now see a clear invitation to test their own apps with Replay QA.',
  },
  {
    date: '2026-06-12',
    title: 'Free QA for publicly shared apps',
    kind: 'milestone',
    summary:
      'The new-project page has a new “For sharing” option: apps you build and post in public forums are QA’ed for free. Already started a project? Add a share location from Settings to refund the credits spent and make it public.',
  },
  {
    date: '2026-06-14',
    title: 'A more detailed usage breakdown',
    kind: 'milestone',
    summary:
      'The Usage section in Settings now lets you expand each app to see its individual metered tasks and what they cost. Journey runs now cost 0.5 credits; explorations and polish passes still cost 1.',
  },
  {
    date: '2026-06-11',
    title: 'No subscription required to get started',
    kind: 'milestone',
    summary:
      'You no longer have to choose a plan up front — without one you simply use your free-tier credits. The plan picker now appears only when your credit balance actually runs out.',
  },
  {
    date: '2026-06-10',
    title: 'Bare URLs in the new-project form',
    kind: 'milestone',
    summary:
      'The App URL field now accepts addresses without a scheme — type your-app.com and Replay treats it as https://your-app.com.',
  },

  // ── Minor updates (shipped between the milestones above) ─────────────────────────────────────
  {
    date: '2026-08-27',
    title: 'API token controls back in the coding-agent guide',
    kind: 'minor',
    summary:
      'The “Start QA from your coding agent” callout again lets you view and manage the API token you need to connect your agent.',
  },
  {
    date: '2026-08-26',
    title: 'Smoother sign-in for shared test-run links',
    kind: 'minor',
    summary:
      'Opening a shared test-run link while signed out now routes you cleanly through sign-in instead of failing to load.',
  },
  {
    date: '2026-08-25',
    title: 'A lighter, faster-loading app',
    kind: 'minor',
    summary:
      'The app now ships a smaller, minified production bundle and defers screens you aren’t using, so the initial load is quicker.',
  },
  {
    date: '2026-08-24',
    title: 'Bug list refreshes when you open it',
    kind: 'minor',
    summary:
      'The Bugs tab now refreshes its list each time you switch to it, so newly filed bugs show up without a manual reload.',
  },
  {
    date: '2026-08-21',
    title: 'Fewer, clearer bug statuses',
    kind: 'minor',
    summary:
      'Removed bug statuses that weren’t used, leaving a shorter, clearer set of states on the Bugs list.',
  },
  {
    date: '2026-08-21',
    title: 'Sharper highlights on wide screenshots',
    kind: 'minor',
    summary:
      'Fixed the alignment of the highlight overlay on wide bug-report screenshots so it lands on the right spot.',
  },
  {
    date: '2026-08-18',
    title: 'Bug reports lead with category and journey',
    kind: 'minor',
    summary:
      'A bug report’s header now shows its category and the journey it came from, so you can place a bug at a glance.',
  },
  {
    date: '2026-08-18',
    title: 'Paused runs now read as paused',
    kind: 'minor',
    summary:
      'When a project is paused, its frozen runs are shown as paused — with the reason available on hover — instead of looking stuck mid-run.',
  },
  {
    date: '2026-08-17',
    title: 'A clearer prompt when your session ends',
    kind: 'minor',
    summary:
      'Signed-out visitors are now sent to the sign-in screen instead of seeing a “Failed to fetch” error.',
  },
  {
    date: '2026-08-16',
    title: 'Clearer test-run results and queue order',
    kind: 'minor',
    summary:
      'Test-run journey lists now read more clearly, including the order in which queued runs will be tested.',
  },
  {
    date: '2026-08-13',
    title: 'See the working app when a round finds nothing',
    kind: 'minor',
    summary:
      'When a QA round finds no issues, the result now shows a screenshot of the working app instead of an empty state.',
  },
  {
    date: '2026-08-05',
    title: 'Chat can read links you paste',
    kind: 'minor',
    summary:
      'The project chat now opens links you paste — web pages and PDFs alike — and reads them in, so you can point QA at a spec, doc, or design while you talk to it.',
  },
  {
    date: '2026-08-05',
    title: 'A PR’s bugs close when the PR does',
    kind: 'minor',
    summary:
      'For GitHub-connected projects, bugs Replay filed against a pull request are now resolved automatically when that pull request is merged or closed.',
  },
  {
    date: '2026-08-05',
    title: 'Test-run cards live in project chat',
    kind: 'minor',
    summary:
      'When QA runs from chat, a test-run card now tracks its progress inline — counting the rounds still running, opening the full run when you click it, and collapsing adjacent runs to keep the conversation readable.',
  },
  {
    date: '2026-08-05',
    title: 'Bugs filter popover no longer clipped',
    kind: 'minor',
    summary:
      'The filter popover on the Bugs list now opens toward the open space instead of being cut off by the edge of the pane.',
  },
  {
    date: '2026-08-04',
    title: 'Test runs and journeys scroll endlessly',
    kind: 'minor',
    summary:
      'Long lists of test runs and journeys now load more as you scroll instead of stopping at the first page.',
  },
  {
    date: '2026-08-04',
    title: 'A faster project overview on big projects',
    kind: 'minor',
    summary:
      'The project overview now streams in section by section and switches tabs noticeably faster on large projects, with cleaner loading states while each part fills in.',
  },
  {
    date: '2026-07-28',
    title: 'Bugs table leads with the bug',
    kind: 'minor',
    summary:
      'The Bugs table now puts each bug’s title first and tightens its columns, and fixes hard-to-read light-grey titles in the project overview’s Bugs list.',
  },
  {
    date: '2026-07-28',
    title: 'Mobile layout fixes',
    kind: 'minor',
    summary:
      'A round of layout fixes so the app reads and taps correctly on small, mobile-sized screens.',
  },
  {
    date: '2026-07-27',
    title: 'Deep links for every Settings tab',
    kind: 'minor',
    summary:
      'Each tab in Settings now has its own URL, so you can bookmark or share a link that opens straight to a specific settings section.',
  },
  {
    date: '2026-07-12',
    title: 'Codex listed alongside Claude Code and Cursor',
    kind: 'minor',
    summary:
      'The “Start QA from your coding agent” guide now mentions Codex too, so all three supported coding agents are covered.',
  },
  {
    date: '2026-07-12',
    title: 'Budget emails link straight to your project',
    kind: 'minor',
    summary:
      'When a project pauses after reaching its credit budget, the email’s button now takes you to the project overview — where you can raise the budget and resume in one click — instead of the billing settings.',
  },
  {
    date: '2026-07-11',
    title: 'Share dialog keeps the Copy button in reach',
    kind: 'minor',
    summary:
      'On shorter screens the project share dialog no longer pushes its link and Copy button out of view — they now stay pinned to the bottom of the dialog as you scroll.',
  },
  {
    date: '2026-07-10',
    title: 'Project thumbnails show your actual app',
    kind: 'minor',
    summary:
      'Cards in the projects gallery now use a real screenshot of the tested app as their thumbnail instead of a generic summary image.',
  },
  {
    date: '2026-07-10',
    title: 'Project cards fit narrow screens',
    kind: 'minor',
    summary:
      'Fixed project gallery cards overflowing the edge of the screen on mobile when a project had a long URL.',
  },
  {
    date: '2026-07-08',
    title: 'Bug Breakdown tooltip stays on top',
    kind: 'minor',
    summary:
      'Fixed the Bug Breakdown chart on the project overview so its hover tooltip is no longer hidden behind the chart’s legend on shorter charts.',
  },
  {
    date: '2026-07-07',
    title: 'Follow-up tasks have their own page',
    kind: 'minor',
    summary:
      'A follow-up task now opens on its own detail page, linked from the run that triggered it, so you can see exactly what it covered.',
  },
  {
    date: '2026-07-07',
    title: 'Clearer next steps in QA result emails',
    kind: 'minor',
    summary:
      'The email you get when QA finishes now includes a tailored “Next Steps” section — for GitHub-connected projects it points you straight at the pull request and its results.',
  },
  {
    date: '2026-07-06',
    title: 'Share button waits for the first analysis',
    kind: 'minor',
    summary:
      'The Share button is now disabled until a project’s initial analysis finishes, so you no longer share a link before there are any results to show.',
  },
  {
    date: '2026-07-03',
    title: 'Failed runs no longer cost credits',
    kind: 'minor',
    summary:
      'Credits are now automatically restored for journey runs that fail or hit an infrastructure error, so you’re only charged for runs that actually complete.',
  },
  {
    date: '2026-07-03',
    title: 'Just one completion email per project',
    kind: 'minor',
    summary:
      'A project’s QA-summary email is now sent at most once instead of repeatedly — it’s re-enabled only when you resume a paused project by adding credits.',
  },
  {
    date: '2026-07-03',
    title: 'Full bug report in the bug detail view',
    kind: 'minor',
    summary:
      'Each bug’s detail view now has a “Full Bug Report” section rendering the complete report as formatted markdown, so you can read the whole write-up without leaving the page.',
  },
  {
    date: '2026-06-27',
    title: 'Snappier project dashboard',
    kind: 'minor',
    summary:
      'The projects dashboard now loads its list faster and shows a cleaner loading state while it fetches.',
  },
  {
    date: '2026-06-24',
    title: 'Clearer Target URL badges',
    kind: 'minor',
    summary:
      'The Target URL badge on tasks, journeys, and explorations is easier to read and now links straight to the exact build it was tested against.',
  },
  {
    date: '2026-06-20',
    title: 'Help widget refresh',
    kind: 'minor',
    summary:
      'Updated the in-app help widget to the latest version for faster search and more relevant articles.',
  },
  {
    date: '2026-06-16',
    title: 'Credit budget control fixes',
    kind: 'minor',
    summary:
      'Fixed a few rough edges in the per-project credit budget control so edits save reliably and the remaining balance always reflects your latest spend.',
  },
  {
    date: '2026-06-13',
    title: 'Usage breakdown polish',
    kind: 'minor',
    summary:
      'The expandable per-app usage breakdown in Settings now totals correctly and keeps each task’s cost aligned as you expand and collapse rows.',
  },

  // (The new chat experience and the redesigned single-page project overview graduated from
  // experimental flags to the default experience for every user on 2026-07-13; their milestone
  // entries are in the milestones section above.)
]

// Display order: newest-first by date. Within the same day, milestones come before minor updates
// so headline features lead. The sort is stable in modern engines, preserving source order for ties.
export const releaseNotes: ReleaseNote[] = [...entries].sort((a, b) => {
  const da = releaseDate(a)
  const db = releaseDate(b)
  if (da !== db) return da < db ? 1 : -1
  if (a.kind !== b.kind) return a.kind === 'milestone' ? -1 : 1
  return 0
})
